// frontend/src/api/teams.ts
import { API_BASE_URL } from '@/config/apiConfig';
import { ApiError } from './auth'; // For consistent error handling
import { createApiClient, isAxiosError } from './client';
import { TeamInLeague, TeamRoster } from './schemas'; // Import necessary schemas

// Create an Axios client instance specifically for team-related endpoints
const teamsApiClient = createApiClient({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Fetches the full roster for a specific team in a league.
 * @param teamId - The ID of the team whose roster is to be fetched.
 * @param token - The authentication token for the current user.
 * @returns A Promise resolving to the TeamRoster object.
 */
export const fetchTeamRoster = async (teamId: number, token: string): Promise<TeamRoster> => {
  if (!token) {
    throw new ApiError("Authentication required to fetch team roster.", 401);
  }
  if (!teamId || teamId <= 0) {
    // It's better to throw an error here that can be caught and displayed,
    // rather than trying to proceed with an invalid teamId.
    console.error("[API] fetchTeamRoster: Invalid Team ID provided:", teamId);
    throw new ApiError("A valid Team ID is required to fetch the roster.", 400);
  }

  const url = `${API_BASE_URL}/teams/${teamId}/roster`; // Path to your backend endpoint
  console.log(`[API] Fetching roster for team ID: ${teamId} from ${url}`);

  try {
    const response = await teamsApiClient.get<TeamRoster>(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log(`[API] Roster data received for team ${teamId}. Players: ${response.data.roster?.length || 0}`);
    
    // Optional: Perform a quick sanity check on the response structure if desired
    if (!response.data || typeof response.data.team_id !== 'number' || !Array.isArray(response.data.roster)) {
        console.error("[API] fetchTeamRoster: Invalid data structure received:", response.data);
        throw new Error("Invalid data structure received for team roster from API.");
    }
    return response.data;

  } catch (error: any) {
    let errorMessage = `Failed to fetch roster for team ${teamId}.`;
    let status: number | undefined;

    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      console.error(`[API] Axios error fetching roster for team ${teamId}:`, {
        message: error.message, url: error.config?.url, status, data: error.response?.data,
      });
      if (typeof backendDetail === 'string') {
        errorMessage = backendDetail;
      } else if (status === 404) {
        errorMessage = "Team or roster not found.";
      } else if (status === 403) {
        errorMessage = "You are not authorized to view this team's roster.";
      } else if (status === 401) {
        errorMessage = "Authentication failed. Please log in again.";
      }
    } else if (error instanceof Error) { // Catch errors from manual throws (like invalid teamId)
      errorMessage = error.message;
      // Status for manually thrown errors might not be set, or you can define one.
      // For an invalid teamId error thrown above, status would be 400.
    } else {
       console.error(`[API] Unexpected error fetching roster for team ${teamId}:`, error);
    }
    throw new ApiError(errorMessage, status); // Re-throw as ApiError for consistent handling
  }
};

// You can add other team-specific API functions here later, for example:
// - updateTeamSettings (e.g., team name, logo)
// - fetchTeamMatchupHistory
// etc.

/**
 * Fetches a team's waiver claims: PENDING claims first (in the owner's
 * self-assigned order, with expected_process_at), then recent history.
 */
export const fetchTeamWaiverClaims = async (teamId: number, token: string): Promise<import('./schemas').WaiverClaimRead[]> => {
  if (!token) {
    throw new ApiError("Authentication required to fetch waiver claims.", 401);
  }
  if (!teamId || teamId <= 0) {
    throw new ApiError("A valid Team ID is required to fetch waiver claims.", 400);
  }

  const url = `${API_BASE_URL}/teams/${teamId}/waiver-claims`;
  try {
    const response = await teamsApiClient.get<import('./schemas').WaiverClaimRead[]>(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch waiver claims for team ${teamId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    throw new ApiError(errorMessage, status);
  }
};

// Claim an open team (manager takeover) using the claim token from a share link.
export const claimOpenTeam = async (teamId: number, claimToken: string, token: string): Promise<import('./schemas').ApiTeamRead> => {
  if (!token) throw new ApiError("Authentication required.", 401);
  const url = `${API_BASE_URL}/teams/${teamId}/claim`;
  try {
    const response = await teamsApiClient.post<import('./schemas').ApiTeamRead>(
      url,
      { claim_token: claimToken },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to claim the team.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    const userMessage = [400, 403, 404].includes(status ?? 0) ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};

// Rename your team (owner only). Backend: PUT /teams/{id}/name.
export const renameTeam = async (teamId: number, name: string, token: string): Promise<TeamInLeague> => {
  if (!token) throw new ApiError("Authentication required.", 401);
  const url = `${API_BASE_URL}/teams/${teamId}/name`;
  try {
    const response = await teamsApiClient.put<TeamInLeague>(
      url,
      { name },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to rename the team.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendDetail = error.response?.data?.detail;
      if (typeof backendDetail === 'string') errorMessage = backendDetail;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    // 400 = the name was rejected (moderation/format) and the user must pick another; 403 = not their team.
    const userMessage = status === 400 || status === 403 ? errorMessage : null;
    throw new ApiError(errorMessage, status, { cause: error, userMessage });
  }
};
